import {useEffect, useMemo, useRef, useState} from 'react';
import {safeAddToArrayUnique} from '@netx/core-next/utils/array';
import {
	type RowData,
	type Table,
	type RowSelectionState,
	type Row,
} from '@tanstack/react-table';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useWhenChangedEffect} from '@netx/core-components/hooks/useWhenChangedEffect';
import {rowSelectionDefaults} from '../defaults';
import type {CustomVirtualizedSelection} from '../types';

// TODO: cleanup/document

export type UseVirtualizedTableRowSelectionParams<TData extends RowData> = {
	excludeIdsFromRowSelection?: string[];
	totalRows: number;
	selection?: CustomVirtualizedSelection;
	onSelectionChange?: (
		selection:
			| CustomVirtualizedSelection
			| ((selection: CustomVirtualizedSelection) => CustomVirtualizedSelection),
	) => void;
};

const saneitizeSelection = (
	selection: CustomVirtualizedSelection,
	totalRows: number,
	excludeIdsFromRowSelection?: string[],
) => {
	const excludeIdsLength = excludeIdsFromRowSelection?.length ?? 0;
	if (totalRows - excludeIdsLength) {
		const _excludedIdsLength = excludeIdsFromRowSelection?.length ?? 0;
		if (selection.includeIds.length === totalRows - _excludedIdsLength) {
			selection.all = true;
			selection.includeIds = [];
			selection.excludeIds = excludeIdsFromRowSelection ?? [];
		}
		if (selection.excludeIds.length === totalRows) {
			selection.all = false;
			selection.excludeIds = excludeIdsFromRowSelection ?? [];
		}
	}
	return selection;
};

export const useVirtualizedTableRowSelection = <TData extends RowData>({
	excludeIdsFromRowSelection,
	totalRows,
	selection: _selection,
	onSelectionChange,
}: UseVirtualizedTableRowSelectionParams<TData>) => {
	const tableRef = useRef<Table<TData>>();
	const lastSelectedIndexes = useRef<number[]>([]);

	const selection = useMemo(() => {
		return {
			...rowSelectionDefaults,
			..._selection,
		};
	}, [_selection]);

	// TODO: this is actually a good case for a reducer
	// const [selection, setSelection] = useState<CustomVirtualizedSelection>({
	// 	all: false,
	// 	includeIds: [],
	// 	excludeIds: [],
	// 	...initialSelection,
	// });

	// taking advantage of the fact that none of their code is doing anything like `map`/`values`/`keys`/etc
	// they simply call on this state object and provide it with a `row.id`
	//
	// TODO: getSelectedRowModel shows empty if you are working from a selected all perspective, is this a problem outside of the selection feature?
	// If not, we just expose and utilize `selection` and do not worry about this.
	/**
	 * This is the state object that is passed to the `useTable` hook under `state`\
	 * This object is derived from our `selection` state and is a proxy to it
	 */
	const reactTableRowSelection = useMemo(() => {
		return new Proxy<RowSelectionState>(
			// this is for `setRowSelection` to work when we are not dealing with `all` selected
			// this allows for it to pass the "previous" data to the updater function and not needing to hack in deeper there
			// (using their props to accomplish this via `state: {rowSelection}`)
			// doubtful someone will manually toggle on thousands of rows, so this should not be costly
			selection.includeIds.reduce((acc, id) => ({...acc, [id]: true}), {}),
			{
				get(target, prop, receiver) {
					if (typeof prop === 'symbol')
						return Reflect.get(target, prop, receiver);
					return (
						(selection.all && !selection.excludeIds.includes(prop)) ||
						selection.includeIds.includes(prop)
					);
				},
			},
		);
	}, [selection]);

	// called from `react-table` via `useTable` props
	const setReactTableRowSelection: Table<TData>['setRowSelection'] =
		useConstCallback((value) => {
			const result =
				typeof value === 'function' ? value(reactTableRowSelection) : value;
			onSelectionChange?.(() => {
				const next = Object.entries(result).reduce<CustomVirtualizedSelection>(
					(acc, [key, value]) => {
						if (value) acc.includeIds.push(key);
						if (!value) acc.excludeIds.push(key);
						return acc;
					},
					{
						all: false,
						includeIds: [],
						excludeIds: [],
					},
				);
				return saneitizeSelection(next, totalRows, excludeIdsFromRowSelection);
			});
		});

	// called from `react-table` via `wrapTable`
	const handleToggleAllRowsSelected = useConstCallback((value?: boolean) => {
		lastSelectedIndexes.current = [];
		const _excludedIdsLength = excludeIdsFromRowSelection?.length ?? 0;
		onSelectionChange?.((rowSelection) => {
			const allSelected =
				totalRows - _excludedIdsLength ? value ?? !rowSelection.all : false;
			return {
				all: allSelected,
				includeIds: [],
				excludeIds: allSelected ? excludeIdsFromRowSelection ?? [] : [],
			};
		});
	});

	useWhenChangedEffect(totalRows, (totalRows, oldTotalRows) => {
		if (!oldTotalRows) return;

		const _excludedIdsLength = excludeIdsFromRowSelection?.length ?? 0;

		if (selection.includeIds.length === totalRows - _excludedIdsLength) {
			onSelectionChange?.((prev) => ({
				all: !!(totalRows - _excludedIdsLength),
				includeIds: [],
				excludeIds: excludeIdsFromRowSelection ?? [],
			}));
		} else {
			onSelectionChange?.((prev) => ({
				...prev,
				all: false,
			}));
		}
	});

	// called from `react-table` via `wrapTable`
	const handleGetIsAllRowsSelected = useConstCallback(() => {
		const activeExcludedIds = selection.excludeIds.filter(
			(id) => !excludeIdsFromRowSelection?.includes(id),
		);
		return selection.all && activeExcludedIds.length === 0;
	});

	// called from `react-table` via `wrapTable`
	const handleIsSomeRowsSelected = useConstCallback(() => {
		const activeExcludedIds = selection.excludeIds.filter(
			(id) => !excludeIdsFromRowSelection?.includes(id),
		);
		return (
			(selection.all && activeExcludedIds.length > 0) ||
			selection.includeIds.length > 0
		);
	});

	// called from `react-table` row via `wrapTable`
	const handleRowToggleSelected = useConstCallback(
		(row: Row<TData>) =>
			(
				value?: boolean,
				opts: {
					selectChildren?: boolean;
					// custom
					shiftKey?: boolean;
				} = {},
			) => {
				if (!row.getCanSelect()) return;

				const {shiftKey} = opts;
				// if shiftKey was pressed we will select everything between the last selected row and this row
				if (shiftKey) {
					// clear any inadvertent text selection
					document.getSelection()?.removeAllRanges();

					const lastIndex = lastSelectedIndexes.current[0];
					if (lastIndex != null) {
						const startIndex = row.index < lastIndex ? row.index : lastIndex;
						const stopIndex = row.index > lastIndex ? row.index : lastIndex;
						(tableRef.current?.getRowModel().rows || [])
							.slice(startIndex, stopIndex + 1)
							.forEach((row) => row?.toggleSelected(true));
						return;
					}
				}

				const isSelected = row.getIsSelected();
				value = value ?? !isSelected;
				if (isSelected === value) return;

				onSelectionChange?.((prev) => {
					// no value means we are toggling from an selected state
					// whether this was `all` or not, we add it to the excluded list
					// and ensure that it is not in the included list
					if (!value) {
						lastSelectedIndexes.current = lastSelectedIndexes.current.filter(
							(index) => index !== row.index,
						);
						return saneitizeSelection(
							{
								...prev,
								excludeIds: safeAddToArrayUnique(prev.excludeIds, row.id),
								includeIds: prev.includeIds.filter((id) => id != row.id),
							},
							totalRows,
							excludeIdsFromRowSelection,
						);
					}

					lastSelectedIndexes.current = [
						...new Set([row.index, ...lastSelectedIndexes.current]),
					];
					lastSelectedIndexes.current.length = Math.min(
						lastSelectedIndexes.current.length,
						10,
					);

					// if `all`, we just need to make sure it is not in the excluded list
					// `includeIds` should always be empty in this case
					return prev.all
						? {
								...prev,
								includeIds: [],
								excludeIds: prev.excludeIds.filter((id) => id != row.id),
						  }
						: // otherwise, we need to make sure it is not in the excluded list
						  // and ensure that it is in the included list
						  saneitizeSelection(
								{
									...prev,
									excludeIds: prev.excludeIds.filter((id) => id != row.id),
									includeIds: safeAddToArrayUnique(prev.includeIds, row.id),
								},
								totalRows,
								excludeIdsFromRowSelection,
						  );
				});
			},
	);

	// wrap our hacks onto the `table` object
	const wrapTable = useConstCallback((table: Table<TData>) => {
		if (tableRef.current === table) return table;

		tableRef.current = table;

		// behold, the _power_ of monkey patching
		// i am doing my best to limit these as each one delivers its own set of issues (primarily in upgrading the library) and is not a great pattern
		table.toggleAllRowsSelected = handleToggleAllRowsSelected;
		table.getIsAllRowsSelected = handleGetIsAllRowsSelected;
		table.getIsSomeRowsSelected = handleIsSomeRowsSelected;

		// work from the last feature to ensure we are overriding the `RowSelection`s method
		// the lib just allows for tacking these on and is not re-returning anthing here
		// `createRow` is checked (and called if set) for on all features, allowing them to add additional methods etc
		// Maybe one day we can create our own custom features: https://github.com/TanStack/table/discussions/4403
		for (let i = table._features.length - 1; i >= 0; i--) {
			const f = table._features[i];
			if (f.createRow != null) {
				const old = f.createRow;
				f.createRow = (row, table) => {
					old.call(f, row, table);
					row.toggleSelected = handleRowToggleSelected(row);
				};
				break;
			}
		}

		return table;
	});

	// console.log('rowSelection', rowSelection);
	// console.log('selection', selection);

	// there is a chicken egg issue where we cannot update the table options correctly after init
	// namely we cannot update the `rowSelection` state object
	// so we provide the state, state updater method AND a wrapper method
	return {
		lastSelectedIndexes,
		reactTableRowSelection,
		setReactTableRowSelection,
		wrapTable,
	};
};
